import { useEffect, useRef, useState } from 'react';
import { Phone, ChevronDown, Search } from 'lucide-react';
import { COUNTRIES, flagUrl, type Country } from '../data/countryCodes';

interface PhoneInputProps {
  /** Currently selected country (controlled). */
  country: Country;
  onCountryChange: (country: Country) => void;
  /** National number, digits only (controlled). */
  value: string;
  onChange: (value: string) => void;
  isDark: boolean;
  placeholder?: string;
  required?: boolean;
}

/** Max national-number digits allowed for a country. India is fixed at 10. */
export function maxDigitsFor(country: Country): number {
  return country.iso === 'in' ? 10 : 15;
}

export default function PhoneInput({
  country,
  onCountryChange,
  value,
  onChange,
  isDark,
  placeholder = '9876543210',
  required,
}: PhoneInputProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on click outside or Escape
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    // Focus the search box when the dropdown opens
    searchRef.current?.focus();
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.dialCode.toLowerCase().includes(q) ||
          c.dialCode.replace('+', '').includes(q.replace('+', '')),
      )
    : COUNTRIES;

  const select = (c: Country) => {
    onCountryChange(c);
    onChange(value.replace(/\D/g, '').slice(0, maxDigitsFor(c)));
    setOpen(false);
    setQuery('');
  };

  const inp = `w-full pl-10 pr-4 py-3 rounded-xl border text-sm outline-none transition-colors ${
    isDark
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-emerald-500'
  }`;

  return (
    <div className="flex gap-2" ref={wrapRef}>
      {/* Country selector */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`flex items-center gap-1.5 h-full px-3 py-3 rounded-xl border text-sm font-semibold transition-colors ${
            isDark
              ? 'bg-gray-800 border-gray-700 text-gray-200 hover:border-gray-600'
              : 'bg-gray-100 border-gray-200 text-gray-700 hover:border-gray-300'
          }`}
        >
          <img
            src={flagUrl(country.iso)}
            alt=""
            width={20}
            height={14}
            className="rounded-sm object-cover"
          />
          <span>{country.dialCode}</span>
          <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div
            className={`absolute z-50 mt-2 w-72 max-h-72 overflow-hidden rounded-xl border shadow-xl ${
              isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            {/* Search */}
            <div className={`p-2 border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
              <div className="relative">
                <Search
                  size={14}
                  className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search country or code"
                  className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm outline-none ${
                    isDark
                      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500'
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-emerald-500'
                  }`}
                />
              </div>
            </div>

            {/* List */}
            <ul role="listbox" className="max-h-56 overflow-y-auto py-1">
              {filtered.length === 0 && (
                <li className={`px-3 py-3 text-sm text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  No countries found
                </li>
              )}
              {filtered.map((c) => {
                const selected = c.iso === country.iso && c.dialCode === country.dialCode;
                return (
                  <li key={`${c.iso}-${c.dialCode}`} role="option" aria-selected={selected}>
                    <button
                      type="button"
                      onClick={() => select(c)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                        selected
                          ? 'bg-emerald-500/10 text-emerald-500'
                          : isDark
                            ? 'text-gray-200 hover:bg-gray-800'
                            : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <img
                        src={flagUrl(c.iso)}
                        alt=""
                        width={20}
                        height={14}
                        className="rounded-sm object-cover shrink-0"
                      />
                      <span className="flex-1 truncate">{c.name}</span>
                      <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{c.dialCode}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* National number */}
      <div className="relative flex-1">
        <Phone size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
        <input
          type="tel"
          inputMode="numeric"
          maxLength={maxDigitsFor(country)}
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, maxDigitsFor(country)))}
          placeholder={placeholder}
          className={inp}
          required={required}
        />
      </div>
    </div>
  );
}
